import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BriefcaseBusiness, ChevronDown } from "lucide-react";
import { useCategories } from "../../../hooks/category";
import type { StepCompanyProps } from "../../../core/types/tsx/components/wizard/provider-registration/StepCompany.types";

type ApiCategory = {
	id: number;
	name_category: string;
};

export function StepCompany({
	company,
	setCompany,
	errors,
	clearError,
	inputBase,
	inputWithIcon,
	errorText,
	showTerms = true,
}: StepCompanyProps) {
	const { data: categoriesData } = useCategories();
	const categoryOptions = useMemo<ApiCategory[]>(() => {
		const source = Array.isArray(categoriesData) ? categoriesData : [];
		return source
			.filter((category) => category.is_active !== false)
			.map((category) => ({
				id: Number(category.id),
				name_category: String(category.name_category ?? "").trim(),
			}))
			.filter((category) => category.id > 0 && category.name_category.length > 0);
	}, [categoriesData]);
	const [openCategories, setOpenCategories] = useState(false);
	const [selectionError, setSelectionError] = useState<string | null>(null);

	const selectedCategoryLabels = useMemo(
		() =>
			categoryOptions
				.filter((item) => company.categoryIds.includes(item.id))
				.map((item) => item.name_category),
		[categoryOptions, company.categoryIds],
	);

	const toggleCategory = (categoryId: number) => {
		setSelectionError(null);
		clearError("categorias");

		setCompany((current) => {
			const alreadySelected = current.categoryIds.includes(categoryId);
			if (alreadySelected) {
				return {
					...current,
					categoryIds: current.categoryIds.filter((id) => id !== categoryId),
				};
			}

			return {
				...current,
				categoryIds: [...current.categoryIds, categoryId],
			};
		});
	};

	return (
		<>
			<div className='space-y-2'>
				<label className='block text-xs font-medium text-slate-600'>
					Agregar tu categoría
				</label>
				<div className='relative'>
					<button
						type='button'
						onClick={() => setOpenCategories((v) => !v)}
						className={
							inputBase +
							" cursor-pointer flex items-center justify-between text-left"
						}
					>
						<span
							className={
								selectedCategoryLabels.length > 0
									? "text-slate-700"
									: "text-slate-400"
							}
						>
							{selectedCategoryLabels.length > 0
								? selectedCategoryLabels.join(", ")
								: "Selecciona una o más categorías"}
						</span>
						<ChevronDown
							className={
								"h-5 w-5 text-slate-600 transition-transform " +
								(openCategories ? "rotate-180" : "")
							}
						/>
					</button>

					{openCategories ? (
						<div className='absolute z-20 mt-2 w-full rounded-lg border border-slate-200 bg-white shadow-lg'>
							<div className='max-h-56 overflow-auto py-1'>
								{categoryOptions.length === 0 ? (
									<p className='px-3 py-2 text-sm text-slate-500'>
										No hay categorías disponibles
									</p>
								) : (
									categoryOptions.map((category) => {
										const selected = company.categoryIds.includes(
											category.id,
										);
										return (
											<button
												key={category.id}
												type='button'
												onClick={() =>
													toggleCategory(category.id)
												}
												className='cursor-pointer flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
											>
												<span
													className={
														"inline-flex h-4 w-4 items-center justify-center rounded border text-xs " +
														(selected
															? "border-primary bg-primary text-white"
															: "border-slate-300 bg-white text-transparent")
													}
												>
													✓
												</span>
												<span>{category.name_category}</span>
											</button>
										);
									})
								)}
							</div>
						</div>
					) : null}
				</div>
				<p className='text-xs text-slate-500'>Selección ilimitada</p>
				{errors.categorias ? (
					<p className={errorText}>{errors.categorias}</p>
				) : null}
				{!errors.categorias && selectionError ? (
					<p className={errorText}>{selectionError}</p>
				) : null}
			</div>

			<div className='space-y-2'>
				<label className='block text-xs font-medium text-slate-600'>
					Nombre de tu Empresa/Negocio (opcional)
				</label>
				<div className='relative'>
					<BriefcaseBusiness className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
					<input
						type='text'
						value={company.nombreEmpresa}
						onChange={(e) => {
							clearError("nombreEmpresa");
							setCompany((c) => ({ ...c, nombreEmpresa: e.target.value }));
						}}
						placeholder='Ej: Carpintería Pérez'
						className={inputWithIcon}
					/>
				</div>
				{errors.nombreEmpresa ? (
					<p className={errorText}>{errors.nombreEmpresa}</p>
				) : null}
			</div>

			<div className='space-y-2'>
				<label className='block text-xs font-medium text-slate-600'>
					Descripción de Empresa/Servicios
				</label>
				<textarea
					value={company.descripcion}
					onChange={(e) => {
						clearError("descripcion");
						setCompany((c) => ({ ...c, descripcion: e.target.value }));
					}}
					placeholder='Cuéntanos qué haces, tu especialidad y cómo trabajas (empresa o independiente)...'
					rows={5}
					className={inputBase + " resize-none"}
				/>
				{errors.descripcion ? (
					<p className={errorText}>{errors.descripcion}</p>
				) : null}
			</div>

			{showTerms ? (
				<div className='pt-2 text-center text-xs text-slate-500'>
					Al registrarte, aceptas nuestros{" "}
					<Link
						className='text-primary hover:text-primary-hover'
						to='/terminos-condiciones'
					>
						Términos de Servicio
					</Link>{" "}
					y{" "}
					<Link
						className='text-primary hover:text-primary-hover'
						to='/politicas'
					>
						Política de Privacidad
					</Link>
				</div>
			) : null}
		</>
	);
}
